import { message, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";

import { BTPopover } from "@btnet/core-components/BTPopover";
import { BTStatusBadge } from "@btnet/core-components/BTStatusBadge";
import { BTText } from "@btnet/core-components/BTText";
import { accountingLinkingAccountingLinkingOptionsByEntityIdAndIdTypeGetV2 } from "@btnet/services-btnet/handlers/accounting-linking/accounting-linking";
import { IDTypes } from "@btnet/services-btnet/handlers/models/iDTypes";
import type { StringBTServiceListItem } from "@btnet/services-btnet/handlers/models/stringBTServiceListItem";
import { cn } from "@btnet/utilities-core";

import {
    BdsButton,
    BdsButtonGroup,
    BdsFormItem,
    BdsIconArrowsClockwise,
    BdsIconArrowSquareOut,
    BdsIconExclamationCircle,
    BdsIconLinkSimple,
    BdsIconMinus,
    BdsIconX,
    BdsRollupButton,
} from "@buildertrend/components";

import { BTSelectItem } from "types/apiResponse/apiResponse";
import { AccountingIntegrationType } from "types/enum";

import { showAPIErrorMessage } from "utilities/apiHandler";
import { getSelectedValue, useActionBeingPerformed } from "utilities/form/form";
import { isNullOrUndefined } from "utilities/object/object";

import BdsSectionBox from "commonComponents/bdsWrappers/BdsSectionBox/BdsSectionBox";
import { btConfirm } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTExternalLink } from "commonComponents/btWrappers/BTExternalLink/BTExternalLink";
import { BTIconPlus } from "commonComponents/btWrappers/BTIcon";
import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { LinkingAction } from "commonComponents/entity/accounting/AccountingLinkingOptions/AccountingLinkingOptions.api.types";
import {
    BTPrimaryEntityOption,
    defaultAccountingSelectValue,
    type IAccountingEntity,
    type PrimaryAccountingOptionsExtraData,
    type SecondaryAccountingOptionsExtraData,
    type UnlinkAccountingResponse,
    type UpdateAccountingResponse,
} from "commonComponents/entity/accounting/LinkedAccountingEntity/LinkedAccountingEntity.api.types";
import {
    EntityTypeNames,
    getIntegrationTypeName,
    IntegrationTypeJobLinkingHelpServiceCloudUrls,
} from "commonComponents/financial/Common/Accounting.types";
import { getFullIntegrationName } from "commonComponents/financial/Common/accountingUtils";
import { SplitRow } from "commonComponents/utilities/SplitRow/SplitRow";
import { SplitRowWithDotLeader } from "commonComponents/utilities/SplitRowWithDotLeader/SplitRowWithDotLeader";
import TextTruncate from "commonComponents/utilities/TextTruncate/TextTruncate";

import { AccountingLinkingCardFailed } from "entity/accounting/AccountingLinkingCard/AccountingLinkingCardFailed";
import { JobLinkingTypes } from "entity/accounting/LinkJobToAccounting/LinkJobToAccounting.api.types";

import "./AccountingLinkingCard.less";

import { AccountingLinkingCardSkeleton } from "./AccountingLinkingCardSkeleton";

type AccountingLinkingCardFormActions = "link" | "edit" | "update" | "unlink";
interface IAccountingLinkingCardProps {
    entity?: IAccountingEntity | null;
    fetchEntity?: () => Promise<void>;
    linkByDefault: boolean;
    isNew: boolean;
    btEntityName: string;
    idType: IDTypes;
    disableCreateNew?: boolean;
    errorValidationText?: string;
    btPrimaryEntityOptions?: BTPrimaryEntityOption[];
    primaryFieldName?: string;
    secondaryFieldName?: string;
    selectedPrimaryValue?: string;
    contactFieldName?: string;
    actionFieldName?: string;
    linkTypeFieldName?: string;
    onCancel?: () => void;
    onFieldChange?: (
        field: string,
        value: string | LinkingAction | JobLinkingTypes | null | undefined,
    ) => void;
    onUpdateAccounting?: (entityId: number) => Promise<UpdateAccountingResponse>;
    onUnlinkFromAccounting?: (entityId: number) => Promise<UnlinkAccountingResponse>;
    onReloadAccounting?: () => Promise<void>;
    onLinkEntityToAccounting?: (
        primaryOption: string,
        secondaryOption?: string,
        linkingType?: JobLinkingTypes | null,
        associatedContact?: string,
    ) => Promise<void>;
    showErrorState?: boolean;
    hasAccountingLoaded?: boolean;
}

const linkTypeTerm = (entity: IAccountingEntity, jobLinkType?: JobLinkingTypes | null) => {
    switch (jobLinkType) {
        case JobLinkingTypes.Job:
            return entity.secondaryAccountingTerm;
        case JobLinkingTypes.Project:
            return entity.projectsAccountingTerm;
        default:
            return entity.primaryAccountingTerm;
    }
};

const LinkedAccountingEntityContent: React.FC<{
    entity: IAccountingEntity;
    jobLinkType: JobLinkingTypes | null;
}> = ({ entity, jobLinkType }) => {
    const isLinkingToSecondaryEntity =
        !isNullOrUndefined(jobLinkType) &&
        [JobLinkingTypes.Job, JobLinkingTypes.Project].includes(jobLinkType);
    const isUnavailableEntity = isLinkingToSecondaryEntity
        ? isNullOrUndefined(entity.secondaryAccountingName) // secondaryAccountingName only populated for Job/Projects
        : isNullOrUndefined(entity.primaryAccountingName);
    if (!isUnavailableEntity) {
        // entity is valid, return entity name
        return (
            <TextTruncate>
                {jobLinkType === JobLinkingTypes.Customer ||
                jobLinkType === JobLinkingTypes.SelectAccountingLinkingType
                    ? entity.primaryAccountingName
                    : entity.secondaryAccountingName}
            </TextTruncate>
        );
    }

    const invalidEntityType = linkTypeTerm(entity, jobLinkType);
    const popoverMessage = !entity.isEntityInPendingSync
        ? `We're unable to retrieve this ${invalidEntityType} information. It may have been deleted or deactivated in ${getIntegrationTypeName(
              entity.accountingIntegrationType,
          )}.`
        : null;
    const invalidText = entity.isEntityInPendingSync
        ? `${invalidEntityType} is in pending sync.`
        : `${invalidEntityType} not available`;
    const invalidIcon = (
        <>
            {entity.isEntityInPendingSync ? (
                <BdsIconArrowsClockwise className="warning-icon" />
            ) : (
                <BdsIconExclamationCircle className="error-icon" />
            )}
        </>
    );

    return (
        <>
            <Typography.Text>{invalidText}</Typography.Text>
            <BTPopover content={popoverMessage} disabled={entity.isEntityInPendingSync}>
                {invalidIcon}
            </BTPopover>
        </>
    );
};

export const AccountingLinkingCard: React.FunctionComponent<IAccountingLinkingCardProps> = ({
    entity,
    fetchEntity,
    isNew,
    linkByDefault,
    btEntityName,
    idType,
    disableCreateNew,
    errorValidationText,
    btPrimaryEntityOptions,
    hasAccountingLoaded,
    primaryFieldName = "primaryAccountingSelect",
    secondaryFieldName = "secondaryAccountingSelect",
    selectedPrimaryValue,
    contactFieldName = "accountingContactId",
    actionFieldName = "linkingAction",
    linkTypeFieldName = "jobLinkType",
    onCancel,
    showErrorState = false,
    onFieldChange,
    onUnlinkFromAccounting,
    onUpdateAccounting,
    onLinkEntityToAccounting,
    onReloadAccounting,
}) => {
    // ALL HOOKS AT TOP LEVEL - NEVER CONDITIONAL
    const [actionBeingPerformed, setActionBeingPerformed] =
        useActionBeingPerformed<AccountingLinkingCardFormActions>();
    const [isLinking, setIsLinking] = useState(false);
    // base lists loaded from server or passed in from props
    const [initialPrimaryAccountingList, setInitialPrimaryAccountingList] = useState<
        BTSelectItem<PrimaryAccountingOptionsExtraData>[]
    >([]);
    const [initialSecondaryAccountingList, setInitialSecondaryAccountingList] = useState<
        BTSelectItem<SecondaryAccountingOptionsExtraData>[]
    >([]);
    // lists used for rendering
    const [primaryAccountingList, setPrimaryAccountingList] = useState<
        BTSelectItem<PrimaryAccountingOptionsExtraData>[]
    >([]);
    const [filteredSecondaryList, setFilteredSecondaryList] = useState<
        BTSelectItem<SecondaryAccountingOptionsExtraData>[]
    >([]);
    const [selectedPrimaryOption, setSelectedPrimaryOption] = useState<string>(
        defaultAccountingSelectValue.toString(),
    );
    const [selectedSecondaryOption, setSelectedSecondaryOption] = useState<string>(
        defaultAccountingSelectValue.toString(),
    );
    const [showHelperText, setShowHelperText] = useState<boolean>(
        selectedPrimaryOption === defaultAccountingSelectValue.toString() &&
            !isNullOrUndefined(errorValidationText),
    );
    const [isErrorTouched, setIsErrorTouched] = useState<boolean>(false);
    const [btEntityNameState, setBtEntityNameState] = useState<string>(btEntityName);

    // filter secondary list based on the selected entities
    const getRelatedSecondaryOptions = useCallback(
        (
            secondaryOptions: BTSelectItem<SecondaryAccountingOptionsExtraData>[],
            primarySelection: string,
            secondarySelection: string,
        ) => {
            // primary and secondary options are not related for sage, no need to filter
            if (entity?.accountingIntegrationType === AccountingIntegrationType.SageIntacct) {
                return secondaryOptions;
            }

            // if secondary option is selected, filter to secondary option related to the selected primary option
            if (
                secondarySelection &&
                secondarySelection !== defaultAccountingSelectValue.toString()
            ) {
                return secondaryOptions.filter(
                    (job) =>
                        !isNullOrUndefined(job.extraData) &&
                        job.extraData.relatedCustomer === primarySelection,
                );
            }
            // if primary option is selected, filter to secondary options related to the selected primary option with create option
            if (primarySelection && primarySelection !== defaultAccountingSelectValue.toString()) {
                return secondaryOptions.filter(
                    (job) =>
                        isNullOrUndefined(job.extraData) ||
                        job.extraData.relatedCustomer === primarySelection,
                );
            }
            // otherwise return only the create new option
            return secondaryOptions.filter(
                (job) => job.value === defaultAccountingSelectValue.toString(),
            );
        },
        [entity?.accountingIntegrationType],
    );

    const handleUpdateDropdownLists = useCallback(
        (
            primaryOptions: BTSelectItem<PrimaryAccountingOptionsExtraData>[],
            secondaryOptions: BTSelectItem<SecondaryAccountingOptionsExtraData>[],
        ) => {
            setPrimaryAccountingList(primaryOptions);
            const primaryOption = getSelectedValue(primaryOptions);
            setSelectedPrimaryOption(primaryOption);
            onFieldChange?.(primaryFieldName, primaryOption);
            const secondaryOption = getSelectedValue(secondaryOptions);
            setSelectedSecondaryOption(getSelectedValue(secondaryOptions));
            onFieldChange?.(secondaryFieldName, secondaryOption);
            const relatedJobs = getRelatedSecondaryOptions(
                secondaryOptions,
                primaryOption,
                secondaryOption,
            );
            setFilteredSecondaryList(relatedJobs);
            const selectedPrimaryOption = primaryOptions.find(
                (customer) => customer.value === primaryOption,
            );
            const selectedSecondaryOption = secondaryOptions.find(
                (job) => job.value === secondaryOption,
            );

            // Sage links the job contact to the secondary field (customer)
            if (entity?.accountingIntegrationType === AccountingIntegrationType.SageIntacct) {
                if (selectedSecondaryOption?.extraData) {
                    onFieldChange?.(
                        contactFieldName,
                        selectedSecondaryOption.extraData.relatedCustomer,
                    );
                } else {
                    onFieldChange?.(contactFieldName, defaultAccountingSelectValue.toString());
                }
            } else {
                if (selectedPrimaryOption?.extraData) {
                    onFieldChange?.(contactFieldName, selectedPrimaryOption.extraData.btEntityId);
                } else {
                    onFieldChange?.(contactFieldName, defaultAccountingSelectValue.toString());
                }
                if (selectedSecondaryOption?.extraData) {
                    onFieldChange?.(
                        linkTypeFieldName,
                        selectedSecondaryOption.extraData.jobLinkType,
                    );
                } else {
                    onFieldChange?.(linkTypeFieldName, entity?.jobLinkPreference);
                }
            }

            const linkingAction =
                primaryOption === defaultAccountingSelectValue.toString() &&
                (isNullOrUndefined(secondaryOption) ||
                    secondaryOption === defaultAccountingSelectValue.toString())
                    ? LinkingAction.Create
                    : LinkingAction.Choose;

            onFieldChange?.(actionFieldName, isLinking ? linkingAction : LinkingAction.None);
        },
        [
            onFieldChange,
            primaryFieldName,
            secondaryFieldName,
            getRelatedSecondaryOptions,
            entity?.accountingIntegrationType,
            entity?.jobLinkPreference,
            actionFieldName,
            isLinking,
            contactFieldName,
            linkTypeFieldName,
        ],
    );

    const loadAccountingOptions = useCallback(async () => {
        if (!entity) return false;
        let success = false;
        await setActionBeingPerformed({
            actionBeingPerformed: "edit",
            callback: async () => {
                try {
                    const response =
                        await accountingLinkingAccountingLinkingOptionsByEntityIdAndIdTypeGetV2(
                            entity.id,
                            idType,
                        );
                    const primaryOptions =
                        response.data.primaryAccountingOptions?.map(
                            (option: StringBTServiceListItem) =>
                                new BTSelectItem<PrimaryAccountingOptionsExtraData>(option),
                        ) ?? [];
                    const secondaryOptions =
                        response.data.secondaryAccountingOptions?.map(
                            (option: StringBTServiceListItem) =>
                                new BTSelectItem<SecondaryAccountingOptionsExtraData>(option),
                        ) ?? [];

                    setInitialSecondaryAccountingList(secondaryOptions);
                    setInitialPrimaryAccountingList(primaryOptions);
                    success = true;
                } catch (e) {
                    showAPIErrorMessage(e);
                }
            },
        });
        return success;
    }, [entity, idType, setActionBeingPerformed]);

    const mapBTPrimaryEntityToAccountingOptions = useCallback(
        (primaryOptions: BTSelectItem<PrimaryAccountingOptionsExtraData>[]) => {
            if (entity?.accountingIntegrationType === AccountingIntegrationType.SageIntacct) {
                const options = primaryOptions.map((option) => ({
                    ...option,
                    selected:
                        selectedPrimaryValue !== defaultAccountingSelectValue.toString()
                            ? option.value === selectedPrimaryValue
                            : option.selected,
                }));
                return options;
            }

            const primaryEntityOptions = isNullOrUndefined(btPrimaryEntityOptions)
                ? [new BTPrimaryEntityOption(btEntityName)]
                : btPrimaryEntityOptions;
            if (primaryEntityOptions.length > 0) {
                let newOptionIndex = defaultAccountingSelectValue;
                const defaultOption = primaryOptions.find(
                    (option) => option.id === defaultAccountingSelectValue.toString(),
                );
                const newBTPrimaryEntityOptions: BTSelectItem<PrimaryAccountingOptionsExtraData>[] =
                    [];
                const existingOptions = primaryOptions
                    .filter(
                        (option) =>
                            option.id !== defaultAccountingSelectValue.toString() &&
                            isNullOrUndefined(option.extraData),
                    )
                    .map((option) => ({ ...option, selected: false }));
                primaryEntityOptions.forEach((btPrimaryEntityoption) => {
                    const matchingOptionIndex = existingOptions?.findIndex(
                        (option) =>
                            option.title.toLowerCase() === btPrimaryEntityoption.name.toLowerCase(),
                    );
                    if (matchingOptionIndex !== -1) {
                        existingOptions[matchingOptionIndex].selected = true;
                    } else {
                        // If entity does not support creating new entities, use the existing default option
                        // Otherwise, create a new option with the bt entity's name
                        const newDefaultOption =
                            disableCreateNew && defaultOption
                                ? defaultOption
                                : new BTSelectItem<PrimaryAccountingOptionsExtraData>({
                                      id: newOptionIndex.toString(),
                                      title: btPrimaryEntityoption.name,
                                      extraData: {
                                          btEntityId: btPrimaryEntityoption.btEntityId?.toString(),
                                      },
                                  });
                        newBTPrimaryEntityOptions.push(newDefaultOption);
                        newOptionIndex--;
                    }
                });
                // if no match when remapping the primary buildertrend entities, then select the first new one that was added
                const hasSelectedOption = existingOptions?.some((option) => option.selected);
                if (!hasSelectedOption) {
                    newBTPrimaryEntityOptions[0].selected = true;
                    const hasBTEntityId = !isNullOrUndefined(
                        newBTPrimaryEntityOptions[0].extraData?.btEntityId,
                    );
                    hasBTEntityId || isNullOrUndefined(errorValidationText)
                        ? setShowHelperText(false)
                        : setShowHelperText(true);
                }

                return [...newBTPrimaryEntityOptions, ...existingOptions];
            } else {
                setShowHelperText(true);
                return primaryOptions;
            }
        },
        [
            btPrimaryEntityOptions,
            disableCreateNew,
            btEntityName,
            errorValidationText,
            entity?.accountingIntegrationType,
            selectedPrimaryValue,
        ],
    );

    const mapBTSecondaryEntityToAccountingOptions = useCallback(
        (secondaryOptions: BTSelectItem<SecondaryAccountingOptionsExtraData>[]) => {
            if (entity?.accountingIntegrationType === AccountingIntegrationType.SageIntacct) {
                let existingSecondaryOptions = secondaryOptions.map((option) => ({
                    ...option,
                    selected: option.value === selectedSecondaryOption,
                    title:
                        option.value === defaultAccountingSelectValue.toString()
                            ? ""
                            : option.title,
                }));
                // Use the BT entity's name (contact for jobs) for the "Create new" dropdown option.
                // If no associated BT entity, just return existing options
                if (
                    isNullOrUndefined(btPrimaryEntityOptions) ||
                    btPrimaryEntityOptions.length === 0
                ) {
                    return existingSecondaryOptions;
                }

                // Remove the default "Create new" option
                existingSecondaryOptions = existingSecondaryOptions
                    .filter(
                        (option) =>
                            option.id !== defaultAccountingSelectValue.toString() &&
                            isNullOrUndefined(option.extraData),
                    )
                    .map((option) => ({ ...option, selected: false }));

                // check if there's a match to existing Sage customer
                const existingCustomerIndex = existingSecondaryOptions.findIndex(
                    (option) =>
                        option.title.trim().toLowerCase() ===
                        btPrimaryEntityOptions[0].name.trim().toLowerCase(),
                );

                if (existingCustomerIndex !== -1) {
                    existingSecondaryOptions[existingCustomerIndex].selected = true;
                    return existingSecondaryOptions;
                } else {
                    // Create new option with the contact's name
                    const newDefaultSecondaryOption =
                        new BTSelectItem<SecondaryAccountingOptionsExtraData>({
                            id: defaultAccountingSelectValue,
                            value: defaultAccountingSelectValue.toString(),
                            title: btPrimaryEntityOptions[0].name,
                            selected: true,
                            extraData: {
                                relatedCustomer: btPrimaryEntityOptions[0].btEntityId?.toString(),
                            },
                        });
                    return [newDefaultSecondaryOption, ...existingSecondaryOptions];
                }
            } else {
                return secondaryOptions.map((option) => ({
                    ...option,
                    title:
                        option.value === defaultAccountingSelectValue.toString()
                            ? btEntityName
                            : option.title,
                }));
            }
        },
        [
            entity?.accountingIntegrationType,
            btPrimaryEntityOptions,
            selectedSecondaryOption,
            btEntityName,
        ],
    );

    // update state when entity is linked
    useEffect(() => {
        if (linkByDefault) {
            setIsLinking(true);
        } else {
            setIsLinking(false);
        }
    }, [linkByDefault]);

    const handleLoadAccountingOptions = useCallback(async () => {
        if (initialPrimaryAccountingList.length === 0) {
            return await loadAccountingOptions();
        }
        return true;
    }, [initialPrimaryAccountingList, loadAccountingOptions]);

    // set intial dropdown states for new entities that show the dropdowns by default
    useEffect(() => {
        if (
            entity &&
            (!entity.isEntityLinkedToAccounting || isLinking) &&
            entity.primaryAccountingOptions.length > 0
        ) {
            setInitialSecondaryAccountingList(entity.secondaryAccountingOptions);
            setInitialPrimaryAccountingList(entity.primaryAccountingOptions);
        }
    }, [entity, isLinking]);

    // update options when the buildertrend entity name changes
    useEffect(() => {
        // skip if no options or state list have already been built
        if (
            initialPrimaryAccountingList.length === 0 ||
            (primaryAccountingList.length > 0 && btEntityNameState === btEntityName)
        ) {
            return;
        }
        let primaryOptions = mapBTPrimaryEntityToAccountingOptions(initialPrimaryAccountingList);
        let secondaryOptions = mapBTSecondaryEntityToAccountingOptions(
            initialSecondaryAccountingList,
        );
        if (entity?.accountingIntegrationType !== AccountingIntegrationType.SageIntacct) {
            const existingJobIndex = secondaryOptions?.findIndex(
                (option) => option.title === btEntityName && !isNullOrUndefined(option.extraData),
            );
            // job exists so select it and the related customer
            if (existingJobIndex !== -1) {
                const relatedCustomerId =
                    secondaryOptions[existingJobIndex].extraData?.relatedCustomer;
                secondaryOptions = secondaryOptions.map((option) => ({
                    ...option,
                    selected:
                        option.value === defaultAccountingSelectValue.toString()
                            ? false
                            : option.title === btEntityName && !isNullOrUndefined(option.extraData),
                }));
                primaryOptions = primaryOptions.map((option) => ({
                    ...option,
                    selected: option.id === relatedCustomerId,
                }));
                setShowHelperText(false);
            }
            // select default secondary option with current primary option
            else {
                secondaryOptions = secondaryOptions.map((option) => ({
                    ...option,
                    selected: option.value === defaultAccountingSelectValue.toString(),
                }));
            }
        }
        // update the lists in state
        handleUpdateDropdownLists(primaryOptions, secondaryOptions);
        setBtEntityNameState(btEntityName);
    }, [
        initialPrimaryAccountingList,
        initialSecondaryAccountingList,
        handleUpdateDropdownLists,
        btEntityName,
        mapBTPrimaryEntityToAccountingOptions,
        mapBTSecondaryEntityToAccountingOptions,
        setShowHelperText,
        primaryAccountingList,
        btEntityNameState,
        entity?.accountingIntegrationType,
    ]);

    const handleLinkToAccountingClick = useCallback(async () => {
        if (!entity) return;
        await setActionBeingPerformed({
            actionBeingPerformed: "link",
            callback: async () => {
                let linkType = entity.jobLinkPreference;
                const primarySelection = primaryAccountingList.find(
                    (option) => option.value === selectedPrimaryOption,
                );
                const secondarySelection = selectedSecondaryOption
                    ? filteredSecondaryList.find(
                          (option) => option.value === selectedSecondaryOption,
                      )
                    : null;
                if (secondarySelection && !isNullOrUndefined(secondarySelection.extraData)) {
                    linkType = secondarySelection.extraData.jobLinkType ?? entity.jobLinkPreference;
                }
                const btEntityId = primarySelection?.extraData?.btEntityId;
                if (
                    selectedPrimaryOption === defaultAccountingSelectValue.toString() &&
                    (disableCreateNew ||
                        (!isNullOrUndefined(btPrimaryEntityOptions) &&
                            isNullOrUndefined(btEntityId)))
                ) {
                    setShowHelperText(true);
                    setIsErrorTouched(true);
                } else {
                    await onLinkEntityToAccounting?.(
                        selectedPrimaryOption,
                        selectedSecondaryOption,
                        linkType,
                        btEntityId,
                    );
                    setIsLinking(false);
                }
            },
        });
    }, [
        btPrimaryEntityOptions,
        disableCreateNew,
        setActionBeingPerformed,
        entity,
        primaryAccountingList,
        selectedSecondaryOption,
        filteredSecondaryList,
        selectedPrimaryOption,
        onLinkEntityToAccounting,
    ]);

    const handleUpdateAccounting = useCallback(async () => {
        if (!entity) return;
        const integrationName = getIntegrationTypeName(entity.accountingIntegrationType);
        let linkTypeText = entity.primaryAccountingTerm;
        if (entity.jobLinkPreference === JobLinkingTypes.Job) {
            linkTypeText += ` and ${entity.secondaryAccountingTerm}`;
        } else if (entity.jobLinkPreference === JobLinkingTypes.Project) {
            linkTypeText += ` and ${entity.projectsAccountingTerm}`;
        }

        await setActionBeingPerformed({
            actionBeingPerformed: "edit",
            callback: async () => {
                btConfirm({
                    title: `Update ${linkTypeText} in ${integrationName}?`,
                    content: `Are you sure you want to update/overwrite the data for the linked ${linkTypeText}?`,
                    okText: "Update",
                    onOk: async () => {
                        try {
                            const response = await onUpdateAccounting?.(entity.id);
                            if (response?.accountingSuccess) {
                                void message.success(response.accountingMessage);
                                await onReloadAccounting?.();
                            } else {
                                void message.error(response?.accountingMessage);
                            }
                        } catch (e) {
                            showAPIErrorMessage(e);
                        }
                    },
                });
            },
        });
    }, [entity, onReloadAccounting, onUpdateAccounting, setActionBeingPerformed]);

    const handleUnlinkFromAccounting = useCallback(async () => {
        if (!entity) return;
        await setActionBeingPerformed({
            actionBeingPerformed: "unlink",
            callback: async () => {
                const integrationName = getIntegrationTypeName(entity.accountingIntegrationType);
                btConfirm({
                    title: `Unlink ${entity.displayName} from ${integrationName}?`,
                    content: `This ${entity.entityTerm.toLowerCase()} will no longer sync to your ${integrationName} account.`,
                    okText: `Unlink ${entity.entityTerm}`,
                    cancelText: "Cancel",
                    okType: "primary",
                    onOk: async () => {
                        try {
                            const response = await onUnlinkFromAccounting?.(entity.id);
                            if (response?.hasInvoicedReceivables) {
                                void message.error(
                                    `Cannot unlink the ${integrationName} ${entity.primaryAccountingTerm} from this ${entity.entityTerm} because there are invoiced Invoices.`,
                                );
                            } else {
                                await onReloadAccounting?.();
                            }
                        } catch (e) {
                            showAPIErrorMessage(e);
                        }
                    },
                });
            },
        });
    }, [entity, onReloadAccounting, onUnlinkFromAccounting, setActionBeingPerformed]);

    // CONDITIONAL RENDERING MUST BE AFTER ALL HOOKS
    if (!hasAccountingLoaded && fetchEntity) {
        return <AccountingLinkingCardFailed onReloadAccounting={async () => await fetchEntity()} />;
    } else if (!entity) {
        return <AccountingLinkingCardSkeleton />;
    }

    // Now we can safely use currentEntity
    const integrationName = getIntegrationTypeName(entity.accountingIntegrationType);
    const isConnected = entity.isEntityLinkedToAccounting;
    const clientName = entity.clientAccountingName;

    let linkTypeText = entity.primaryAccountingTerm;
    if (entity.jobLinkPreference === JobLinkingTypes.Job) {
        linkTypeText += ` and ${entity.secondaryAccountingTerm}`;
    } else if (entity.jobLinkPreference === JobLinkingTypes.Project) {
        linkTypeText += ` and ${entity.projectsAccountingTerm}`;
    }

    const getCardContent = () => {
        if (isLinking) {
            return getLinkingStateContent();
        } else {
            return isConnected ? getConnectedStateContent() : getEmptyStateContent();
        }
    };

    const handlePrimaryFieldTouched = () => {
        const option = primaryAccountingList.find(
            (option) => option.value === selectedPrimaryOption,
        );
        if (
            isNullOrUndefined(option?.extraData?.btEntityId) &&
            selectedPrimaryOption === defaultAccountingSelectValue.toString()
        ) {
            setIsErrorTouched(true);
        }
    };

    const handlePrimaryOptionChange = (field: string, value: string) => {
        if (entity?.accountingIntegrationType === AccountingIntegrationType.SageIntacct) {
            setSelectedPrimaryOption(value);
            onFieldChange?.(field, value);
        } else {
            setShowHelperText(false);
            const linkingAction =
                value === defaultAccountingSelectValue.toString()
                    ? LinkingAction.Create
                    : LinkingAction.Choose;
            const primaryOptions = primaryAccountingList.map((option) => ({
                ...option,
                selected: option.value === value,
            }));
            const secondaryOptions = initialSecondaryAccountingList.map((option) => ({
                ...option,
                title:
                    option.value === defaultAccountingSelectValue.toString()
                        ? btEntityName
                        : option.title,
                selected: option.extraData
                    ? option.extraData.relatedCustomer === value && option.title === btEntityName
                    : false,
            }));
            if (
                secondaryOptions.length > 0 &&
                !secondaryOptions.some((option) => option.selected)
            ) {
                secondaryOptions[0].selected = true;
            }
            handleUpdateDropdownLists(primaryOptions, secondaryOptions);
            onFieldChange?.(actionFieldName, linkingAction);
            onFieldChange?.(field, value);

            const primaryOption = primaryOptions.find((x) => x.selected);
            primaryOption?.value === defaultAccountingSelectValue.toString() &&
            isNullOrUndefined(primaryOption?.extraData?.btEntityId) &&
            !isNullOrUndefined(errorValidationText)
                ? setShowHelperText(true)
                : setShowHelperText(false);
        }
    };

    const handleSecondaryOptionChange = (field: string, value: string) => {
        const selectedSecondaryOption = filteredSecondaryList.find(
            (option) => option.value === value,
        );
        setSelectedSecondaryOption(value);
        onFieldChange?.(field, value);
        onFieldChange?.(actionFieldName, LinkingAction.Choose);
        if (selectedSecondaryOption) {
            const linkingType =
                selectedSecondaryOption.value !== defaultAccountingSelectValue.toString() &&
                !isNullOrUndefined(selectedSecondaryOption.extraData)
                    ? selectedSecondaryOption.extraData.jobLinkType
                    : entity.jobLinkPreference;
            onFieldChange?.(linkTypeFieldName, linkingType);
        }
    };

    const primarySelectRender = (selectItem: BTSelectItem<PrimaryAccountingOptionsExtraData>) => {
        if (
            !disableCreateNew &&
            selectItem.extraData &&
            selectItem.value === defaultAccountingSelectValue.toString()
        ) {
            return (
                <span className="accounting-select-item">
                    <BTIconPlus /> Create <b>{selectItem.title}</b> in {integrationName}
                </span>
            );
        } else {
            return selectItem.title;
        }
    };

    const sageSecondarySelectRender = (
        selectItem: BTSelectItem<SecondaryAccountingOptionsExtraData>,
    ) => {
        if (selectItem.value === defaultAccountingSelectValue.toString()) {
            return (
                <span className="accounting-select-item">
                    <BTIconPlus /> Create <b>{selectItem.title}</b> in {integrationName}
                </span>
            );
        } else {
            return selectItem.title;
        }
    };

    const secondarySelectRender = (
        selectItem: BTSelectItem<SecondaryAccountingOptionsExtraData>,
    ) => {
        if (entity.accountingIntegrationType === AccountingIntegrationType.SageIntacct) {
            // For creating new customers, the "create new" option has extraData populated while existing items do not. Need to use custom render similar to the primarySelectRender
            return sageSecondarySelectRender(selectItem);
        }

        if (selectItem.extraData) {
            return (
                <div className="AccountingLinkingCard-secondary-options-dropdown">
                    <span>{selectItem.title}</span>
                    {entity.showEntityTypeHelperText && selectItem.extraData.jobLinkType && (
                        <span className="link-type-dropdown-text">
                            {linkTypeTerm(entity, selectItem.extraData.jobLinkType)}
                        </span>
                    )}
                </div>
            );
        } else {
            return (
                <span className="accounting-select-item">
                    <BTIconPlus /> Create <b>{selectItem.title}</b> in {integrationName}
                </span>
            );
        }
    };

    const handleEditAccounting = async () => {
        if (await handleLoadAccountingOptions()) {
            // only set linking state if entities were loaded successfully
            setIsLinking(true);
        }
    };

    const handleLinkingState = async (isLinking: boolean) => {
        if (isLinking) {
            const loadAccountingSuccess = await handleLoadAccountingOptions();
            if (loadAccountingSuccess) {
                setIsLinking(true);
            }
        } else {
            setIsLinking(false);
            onFieldChange?.(actionFieldName, LinkingAction.None);
        }
    };

    const integrationTypeJobLinkingHelpUrls = (
        <BTExternalLink
            href={IntegrationTypeJobLinkingHelpServiceCloudUrls.get(
                entity.accountingIntegrationType,
            )}
            isUnderline={true}
            data-testid="helpLink"
        >
            <BTText size="normal-md">Learn more</BTText>
            <BdsIconArrowSquareOut size="sm" className="margin-left-xss external-help-icon" />
        </BTExternalLink>
    );

    const getLinkingStateContent = () => {
        const sageText = "Job will automatically be created as a project and synced in Sage.";
        const integrationText = `Link this ${entity.entityTerm.toLowerCase()} to ${integrationName} by creating a new ${linkTypeText} or selecting existing items.`;
        return (
            <>
                <div className="flex flex-row card-gap">
                    <div className="flex flex-col grow">
                        <Typography.Text
                            data-testid="linkingDescription"
                            className="linkingDescription"
                        >
                            {entity.accountingIntegrationType ===
                            AccountingIntegrationType.SageIntacct
                                ? sageText
                                : integrationText}{" "}
                            {idType === IDTypes.JobID &&
                                entity.accountingIntegrationType !==
                                    AccountingIntegrationType.SageIntacct &&
                                integrationTypeJobLinkingHelpUrls}
                        </Typography.Text>
                    </div>
                </div>

                <div
                    className={cn(
                        "flex flex-row",
                        showHelperText &&
                            selectedPrimaryOption === defaultAccountingSelectValue.toString()
                            ? ""
                            : "card-gap",
                    )}
                >
                    <BdsFormItem
                        htmlFor={primaryFieldName}
                        labelAlign="top"
                        required
                        label={entity.primaryAccountingTerm}
                        data-testid="primaryAccountingTerm"
                        className="bds-grow accounting-select-form-item"
                    >
                        <BTSelect
                            id={primaryFieldName}
                            data-testid="primaryAccountingSelect"
                            value={selectedPrimaryOption}
                            treeData={primaryAccountingList}
                            onChange={handlePrimaryOptionChange}
                            onBlur={handlePrimaryFieldTouched}
                            customItemRender={primarySelectRender}
                            bds
                        />
                    </BdsFormItem>
                </div>
                {selectedPrimaryOption === defaultAccountingSelectValue.toString() &&
                    entity.accountingIntegrationType !== AccountingIntegrationType.SageIntacct &&
                    showHelperText && (
                        <div className="flex flex-row action-button card-gap">
                            <BdsIconX
                                size="sm"
                                className={cn(
                                    showErrorState || isErrorTouched ? "error-text" : "helper-text",
                                )}
                            />

                            <BTText size="normal-sm">
                                <Typography.Text
                                    className={cn(
                                        showErrorState || isErrorTouched
                                            ? "error-text"
                                            : "helper-text",
                                    )}
                                >
                                    {errorValidationText}
                                </Typography.Text>
                            </BTText>
                        </div>
                    )}

                {filteredSecondaryList.length > 0 && (
                    <div className="flex flex-row card-gap">
                        <BdsFormItem
                            label={linkTypeTerm(entity, entity.jobLinkPreference)}
                            required
                            data-testid="secondaryAccountingTerm"
                            htmlFor={secondaryFieldName}
                            labelAlign="top"
                            className="bds-grow accounting-select-form-item"
                        >
                            <BTSelect
                                id={secondaryFieldName}
                                data-testid="secondaryAccountingSelect"
                                value={selectedSecondaryOption}
                                treeData={filteredSecondaryList}
                                disabled={
                                    selectedPrimaryOption ===
                                        defaultAccountingSelectValue.toString() &&
                                    btPrimaryEntityOptions?.length === 0
                                }
                                onChange={handleSecondaryOptionChange}
                                onBlur={() => {}}
                                customItemRender={secondarySelectRender}
                                bds
                            />
                        </BdsFormItem>
                    </div>
                )}

                {!isNew && (
                    <div className="flex flex-row action-button card-gap">
                        <BdsButton
                            onClick={() => {
                                setIsLinking(false);
                                onCancel?.();
                            }}
                            testid="cancelLinkToAccounting"
                            text="Cancel"
                            displayType="secondary"
                            className="margin-right-xs"
                            disabled={!!actionBeingPerformed}
                        />
                        <BdsButton
                            onClick={handleLinkToAccountingClick}
                            testid="linkToAccounting"
                            text="Link"
                            displayType="primary"
                            loading={actionBeingPerformed === "link"}
                            disabled={!!actionBeingPerformed}
                        />
                    </div>
                )}
            </>
        );
    };

    const getEmptyStateContent = () => {
        return (
            <div className="flex flex-row card-gap">
                <div className="flex flex-col grow">
                    <BTText size="normal-md">{`Mitigate double data entry and save time with our ${integrationName} integration. Sync accounting data across devices to make financial processes easier.`}</BTText>
                </div>
                <div className="flex flex-col action-button">
                    <BdsButton
                        onClick={() => handleLinkingState(true)}
                        testid="linkEntityToAccounting"
                        text={`Link ${EntityTypeNames.get(idType)?.singular.toLocaleLowerCase()}`}
                        displayType="secondary"
                        loading={actionBeingPerformed === "edit"}
                        disabled={!!actionBeingPerformed}
                    />
                </div>
            </div>
        );
    };

    const syncButtonText = () => {
        switch (idType) {
            case IDTypes.JobID:
                return "Sync client info";
            case IDTypes.VendorID:
                return "Sync sub/vendor info";
            case IDTypes.EmployeeID:
                return "Sync user info";
            default:
                return "Sync info";
        }
    };

    const getConnectedStateButtons = () => {
        return (
            <BdsButtonGroup
                id="accountingConnectedButtonGroupId"
                testid="accountingConnectedButtonGroup"
                menuTestid="accountingConnectedButtonGroupMenu"
                rollupTestid="accountingConnectedButtonGroupRollup"
                flexAlign="end"
                boundary=".ant-modal-body"
            >
                <BdsRollupButton
                    id="unlinkFromAccountingButtonId"
                    onClick={handleUnlinkFromAccounting}
                    testid="unlinkFromAccounting"
                    text="Unlink"
                    displayType="secondary"
                    loading={actionBeingPerformed === "unlink"}
                    disabled={!!actionBeingPerformed}
                    value="unlinkFromAccounting"
                    maxStage={0}
                />
                <BdsRollupButton
                    id="updateAccountingConnectionButtonId"
                    onClick={handleUpdateAccounting}
                    testid="updateAccountingConnection"
                    text={syncButtonText()}
                    displayType="secondary"
                    className="margin-right-xs"
                    loading={actionBeingPerformed === "update"}
                    disabled={entity.isEntityInPendingSync || !!actionBeingPerformed}
                    value="updateAccountingConnection"
                    maxStage={0}
                />
                <BdsRollupButton
                    id="editAccountingConnectionButtonId"
                    onClick={handleEditAccounting}
                    testid="editAccountingConnection"
                    text="Edit link"
                    displayType="secondary"
                    className="margin-right-xs"
                    loading={actionBeingPerformed === "edit"}
                    disabled={entity.isEntityInPendingSync || !!actionBeingPerformed}
                    value="editAccountingConnection"
                    maxStage={0}
                />
            </BdsButtonGroup>
        );
    };

    const getConnectedStateContent = () => {
        const hasLinkedSecondaryEntity =
            !isNullOrUndefined(entity.jobLinkType) &&
            [JobLinkingTypes.Job, JobLinkingTypes.Project].includes(entity.jobLinkType) &&
            !entity.isEntityInPendingSync;
        const hasClientName = hasLinkedSecondaryEntity && !isNullOrUndefined(clientName);
        const clientNameContent = hasClientName && (
            <BTText size="normal-md" testid="linkedAccountingClient">
                <TextTruncate>{clientName}</TextTruncate>
            </BTText>
        );

        const accountingSecondaryEntityContent = hasLinkedSecondaryEntity && (
            <BTText size="normal-md" className="linked-entity" testid="linkedSecondaryEntity">
                <LinkedAccountingEntityContent
                    entity={entity}
                    jobLinkType={JobLinkingTypes.Customer}
                />
            </BTText>
        );

        return (
            <>
                {entity.accountingIntegrationType === AccountingIntegrationType.SageIntacct ? (
                    <>
                        <SplitRow
                            wrap={false}
                            left={
                                <BTText className="card-gap" size="distinct-md">
                                    Project
                                </BTText>
                            }
                            right={
                                <BTText className="card-gap" size="normal-md">
                                    {entity.displayName}
                                </BTText>
                            }
                        />
                        <SplitRow
                            wrap={false}
                            left={
                                <BTText className="card-gap" size="distinct-md">
                                    {entity.primaryAccountingTerm}
                                </BTText>
                            }
                            right={
                                <BTText className="card-gap" size="normal-md">
                                    {entity.primaryAccountingName}
                                </BTText>
                            }
                        />
                        <SplitRow
                            wrap={false}
                            left={
                                <BTText size="distinct-md">{entity.secondaryAccountingTerm}</BTText>
                            }
                            right={
                                <BTText size="normal-md">{entity.secondaryAccountingName}</BTText>
                            }
                        />
                    </>
                ) : (
                    <>
                        <div className="flex flex-row card-gap">
                            <div className="flex flex-col grow">
                                <BTText size="normal-sm" className="linked-header">
                                    Buildertrend
                                </BTText>
                            </div>
                            <div className="flex flex-col">
                                <BTText size="normal-sm" className="linked-header">
                                    {getFullIntegrationName(entity.accountingIntegrationType)}
                                </BTText>
                            </div>
                        </div>
                        <div className="flex flex-row">
                            <div className="flex flex-col grow">
                                <BTText size="normal-sm" className="header">
                                    {entity.entityTerm}
                                </BTText>
                            </div>
                            <div className="flex flex-col">
                                <BTText size="normal-sm" className="header">
                                    {linkTypeTerm(entity, entity.jobLinkType)}
                                </BTText>
                            </div>
                        </div>
                        <div className="flex flex-row justify-content-between card-gap">
                            <div className="flex flex-col grow entity-column">
                                <SplitRowWithDotLeader
                                    left={
                                        <BTText
                                            size="distinct-md"
                                            className="linked-entity"
                                            testid="linkedJobEntity"
                                        >
                                            <TextTruncate className="linked-entity-text">
                                                {entity.displayName}
                                            </TextTruncate>
                                        </BTText>
                                    }
                                    right={
                                        <BTText testid="linkedPrimaryEntity" size="distinct-md">
                                            <BTExternalLink
                                                id="linkedPrimaryEntity"
                                                data-testid="linkedIconPrimaryEntity"
                                                className="linked-entity accounting-name justify-content-end"
                                                href={entity.externalAccountingLink}
                                                isUnderline
                                            >
                                                <LinkedAccountingEntityContent
                                                    entity={entity}
                                                    jobLinkType={entity.jobLinkType}
                                                />
                                                <BdsIconArrowSquareOut
                                                    className="margin-left-xss"
                                                    size="sm"
                                                />
                                            </BTExternalLink>
                                        </BTText>
                                    }
                                    leaderType="dotted"
                                />
                            </div>
                        </div>
                        <div className="flex flex-row">
                            <div className="flex flex-col grow">
                                {hasClientName && (
                                    <BTText size="normal-sm" className="header">
                                        Client
                                    </BTText>
                                )}
                            </div>
                            {hasLinkedSecondaryEntity && (
                                <div className="flex flex-col">
                                    <BTText size="normal-sm" className="header">
                                        {entity.primaryAccountingTerm}
                                    </BTText>
                                </div>
                            )}
                        </div>
                        <div
                            className={cn(
                                "flex flex-row",
                                idType === IDTypes.JobID ? "card-gap" : "",
                            )}
                        >
                            <div className="flex flex-col grow entity-column">
                                {hasClientName && (
                                    <SplitRowWithDotLeader
                                        left={clientNameContent}
                                        right={accountingSecondaryEntityContent}
                                        leaderType="dotted"
                                    />
                                )}
                            </div>
                            {hasLinkedSecondaryEntity && !hasClientName && (
                                <div className="flex flex-col">
                                    {accountingSecondaryEntityContent}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </>
        );
    };

    const getLogoStyle = () => {
        switch (entity.accountingIntegrationType) {
            case AccountingIntegrationType.QuickBooksOnline:
            case AccountingIntegrationType.QuickBooksDesktop:
                return "quickbooks-logo";
            case AccountingIntegrationType.Xero:
                return "xero-logo";
            case AccountingIntegrationType.SageIntacct:
                return "sage-logo";
            default:
                return "";
        }
    };

    return (
        <BdsSectionBox testid="accountingLinkingCard" className="AccountingLinkingCard">
            <div>
                <div className="flex flex-row">
                    <div className="flex flex-col grow accounting-logo">
                        <img
                            src={entity.accountingIntegrationLogoImgSrc}
                            className={getLogoStyle()}
                            alt={integrationName}
                            loading="lazy"
                        />
                    </div>
                    {(!isLinking ||
                        entity.accountingIntegrationType ===
                            AccountingIntegrationType.SageIntacct) && (
                        <>
                            <div className="flex flex-col">
                                <BTStatusBadge
                                    text={isConnected ? "Linked" : "Not linked"}
                                    testid="accountingLinkBadge"
                                    icon={isConnected ? <BdsIconLinkSimple /> : <BdsIconMinus />}
                                    className="margin-top-sm"
                                    displayType={isConnected ? "info" : "default"}
                                />
                            </div>
                            {isConnected &&
                                entity.accountingIntegrationType !==
                                    AccountingIntegrationType.SageIntacct && (
                                    <div className="flex flex-col margin-left-xs margin-top-xs">
                                        {getConnectedStateButtons()}
                                    </div>
                                )}
                        </>
                    )}
                    {isLinking &&
                        isNew &&
                        entity.accountingIntegrationType !==
                            AccountingIntegrationType.SageIntacct && (
                            <BdsButton
                                displayType="tertiary"
                                icon={<BdsIconX />}
                                ariaLabel="Cancel"
                                id="cancelLinking"
                                onClick={() => handleLinkingState(false)}
                                testid="cancelLinking"
                            />
                        )}
                </div>
                {getCardContent()}
            </div>
        </BdsSectionBox>
    );
};
